import { FullPageLoader } from "lib";
import Head from "next/head";
import { useRouter } from "next/router";
import { createContext, type ReactNode, useContext } from "react";
import { useQuery } from "react-query";
import { client, saveShopUrl } from "utils/api-client";
import { getStoredItem } from "utils/StorageItem";

export type Shop = {
  id: number;
  name: string;
  logo: string;
  favicon: string | null;
  timeZone: string;
  country: string;
  url: string;
  lang: string;
};

const ShopContext = createContext<Shop | undefined>(undefined);

ShopContext.displayName = "ShopContext";

function ShopProvider({ initialShop, ...props }: { initialShop?: Shop; children: ReactNode }) {
  const router = useRouter();
  const { data, error, isLoading, isIdle, isError } = useQuery<Shop, Error>({
    queryKey: ["shop", window.location.hostname],
    queryFn: async () => await client<Shop>(`api/pos/shop/${window.location.hostname}`),
    initialData: initialShop,
    enabled: !initialShop,
    retry: false,
    refetchOnWindowFocus: false,
    onSuccess: (shop) => {
      saveShopUrl(shop.url);
      const { pathname, asPath, query } = router;
      const locale = getStoredItem("lang") ?? shop.lang.substring(0, 2);

      void router.push({ pathname, query }, asPath, {
        locale: locale || "es",
      });
    },
  });

  if (isLoading || isIdle) return <FullPageLoader />;
  if (isError) throw error;

  return (
    <>
      {data.favicon ? (
        <Head>
          <link href={data.favicon} rel="icon" type="image/png" />
        </Head>
      ) : null}
      <ShopContext.Provider value={data} {...props} />
    </>
  );
}

function useShop() {
  const context = useContext(ShopContext);

  if (context === undefined) {
    throw new Error(`useShop must be used inside ShopProvider`);
  }

  return context;
}

export { ShopProvider, useShop };
